import { Component, Input, OnInit } from '@angular/core';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, map } from 'rxjs/operators';

import { Association, AssociationMember, AssociationMemberFilters } from '../../../../../../../models/association';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601 } from '../../../../../../../utils';
import { ListResponse } from '../../../../../../../models/list-response';
import { AssociationService } from '../../../../../../../services/association/association.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { AddAssociationMemberModalComponent } from '../../modals/add-association-member-modal/add-association-member-modal.component';
import { EditAssociationForCustomerModalComponent } from '../../../../customers/customer-detail/modals/edit-association-for-customer-modal/edit-association-for-customer-modal.component';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { CheckInAlertsModalComponent } from '../../../../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-alerts-modal/check-in-alerts-modal.component';
import { CheckInNoEntryMethodModalComponent } from '../../../../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-no-entry-method-modal/check-in-no-entry-method-modal.component';
import { ReviewAssociationJoinRequestModalComponent } from '../../../../inbox/inbox-associations/inbox-association-join-requests/modals/review-association-join-request-modal/review-association-join-request-modal.component';
import { BulkUploadAssociationMemberModalComponent } from '../../modals/bulk-upload-association-member-modal/bulk-upload-association-member-modal.component';


@Component({
  selector: 'app-association-members-sub-list',
  templateUrl: './association-members-sub-list.component.html',
  styleUrls: ['./association-members-sub-list.component.css']
})
export class AssociationMembersSubListComponent implements OnInit {
  @Input() association: Association;
  @Input() resultCallback: (result: CallbackResult) => void;

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeAssociationMembersSubList;
  public filters: AssociationMemberFilters = {
    search: ''
  };

  public filtersChangedSubject = new Subject<null>();

  public memberList: ListResponse<AssociationMember>;

  public loadingMembers = true;
  public loadingCheckInByMemberId: {[key: number]: boolean} = {}

  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    private accountService: AccountService,
    private associationService: AssociationService,
    private customerService: CustomerService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    if (!this.pageSizeAssociationMembersSubList) {
      this.pageSizeAssociationMembersSubList = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeAssociationMembersSubList = this.pageSizeAssociationMembersSubList;

    this.filters.association_id = this.association.id;

    // Handle changes to the filters by reloading the members,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => { this.loadingMembers = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadMembers();
    });

    this.loadMembers();
  }

  public openAddMemberModal() {
    const modalRef = this.modalService.open(AddAssociationMemberModalComponent);
    modalRef.componentInstance.association = this.association;
    modalRef.componentInstance.excludeCustomerIds = [];
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadMembers();
    };
  }

  public openBulkUploadMembersModal() {
    const modalRef = this.modalService.open(BulkUploadAssociationMemberModalComponent);
    modalRef.componentInstance.association = this.association;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadMembers();
    };
    modalRef.componentInstance.reloadCallback = () => {
      this.loadMembers();
    };
  }

  public handleEditMember(member: AssociationMember) {
    const modalRef = this.modalService.open(EditAssociationForCustomerModalComponent);
    modalRef.componentInstance.associationMember = member;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadMembers();
    };
  }

  public handleReviewJoinRequest(member: AssociationMember) {
    const modalRef = this.modalService.open(ReviewAssociationJoinRequestModalComponent);
    modalRef.componentInstance.associationId = member.association_id;
    modalRef.componentInstance.associationMemberId = member.id;
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadMembers();
    };
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.memberList) { return 1; }
    return Math.ceil(this.memberList.count / this.pageSizeAssociationMembersSubList);
  }

  public checkInMemberInit(member: AssociationMember) {
    if (this.loadingCheckInByMemberId[member.id]) { return; }

    this.checkInMember(member, false, null);
  }

  private loadMembers() {
    this.loadingMembers = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.associationService
      .getAssociationMembers(
        this.pageNumber,
        this.pageSizeAssociationMembersSubList,
        this.filters
      )
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.memberList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingMembers = false;
        }
      });
  }

  private checkInMember(member: AssociationMember, bypassAlerts: boolean, freeEntryReason: string | null) {
    this.loadingCheckInByMemberId[member.id] = true;

    this.customerService
      .checkInCustomerViaAssociation(member.customer_id, this.association.id, bypassAlerts, freeEntryReason)
      .then(() => {
        this.resultCallback({
          status: CallbackResultStatus.success,
          text: member.customer_first_name + ' ' + member.customer_last_name +
            ' checked in to ' + this.accountService.getDefaultLocation().name + ' successfully.'
        });
      })
      .catch(reason => {
        if (reason.status === 400) {
          const errorType = reason.error.error;
          if (errorType === 'check_in_alerts') {
            const checkInAlerts = reason.error.alerts;
            const modalRef = this.modalService.open(CheckInAlertsModalComponent);
            modalRef.componentInstance.alerts = checkInAlerts;
            modalRef.componentInstance.checkInAnywayCallback = () => {
              this.checkInMember(member, true, freeEntryReason);
            };
          } else if (errorType === 'no_entry_method') {
            const modalRef = this.modalService.open(CheckInNoEntryMethodModalComponent);
            modalRef.componentInstance.customerId = member.customer_id;
            modalRef.componentInstance.association = this.association;
            modalRef.componentInstance.provideFreeEntryCallback = (thisFreeEntryReason: string) => {
              this.checkInMember(member, bypassAlerts, thisFreeEntryReason);
            };
          } else if (errorType === 'customer_not_member') {
            this.resultCallback({
              status: CallbackResultStatus.error,
              text: 'This customer is no longer a member of the association.'
            });
          }
        } else if (reason.status === 403) {
          this.resultCallback({
            status: CallbackResultStatus.error,
            text: 'You do not have permission to check this customer in.'
          });
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCheckInByMemberId[member.id] = false; });
  }
}
